import AutoPayIcon from '@assets/icons/autopay.svg';
import i18n from '@/utils/i18n';
import PaymentHistoryEmptyStateIcon from '@assets/icons/payment-history-empty-state.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SelectedInvoiceEmptyStateIcon from '@assets/icons/sheet.svg';
import { Action } from '@/components/navigation-header-content';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Analytics } from '@/utils/analytics';
import { ApolloError } from '@apollo/client';
import { AutoPayActionSheet } from '@/components/payments/autopay-action-sheet';
import { BankDepositsContent } from '@/components/payments/bank-deposits-content';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { CreditMemoDetailComponent } from '@/screens/credit-memo-detail/credit-memo-detail-component';
import { CreditRequestInvoiceStoreProvider } from '@/stores/credit-request-invoice-store';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { InvoiceDetailComponent } from '@/screens/invoice-detail/invoice-detail-component';
import { Linking, Platform, RefreshControl, Text, View } from 'react-native';
import { List } from '@/components/list';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { NewAutoPayIcon } from './new-auto-pay-icon';
import {
  NewFeature,
  PaymentItemType,
  PaymentMethodType,
  PaymentsStatus,
} from '@/enums';
import { OfflineComponent } from '@/components/offline-component';
import { OrderDetailComponent } from '@/screens/order-detail/order-detail-component';
import { partnerSupplier } from '@/utils/environment';
import { PAYMENT_ROUTES } from '@/navigators/routes';
import { PaymentBannerContainer } from '@/components/payments/payment-banner-container';
import { PaymentDetailComponent } from '@/components/payments/payment-detail-component';
import { PaymentHistoryList } from '@/components/payments/payment-history-list';
import { PaymentHistoryListItem } from '@/components/payments/payment-history-list-item';
import { PaymentLink } from '@/components/payments/payment-links-list';
import { PaymentLinkDetailComponent } from '@/components/payments/payment-link-detail-component';
import { PaymentLinksComponent } from './payment-links-component';
import { PaymentLinksListItem } from '@/components/payments/payment-links-list-item';
import {
  PaymentsActionListOption,
  usePaymentsScreenMoreActions,
} from '@/hooks/payments/use-payments-screen-more-actions';
import { PaymentsScreenActions } from './payments-screen-actions';
import { PaymentsStackParams } from '@/navigators/types';
import { PaymentTabRoutes } from './payments-screen';
import { Spacing } from '@usepepper/sprout-kit';
import {
  SplitPageContainer,
  useSharedStyles as useSplitContainerStyles,
} from '@/components/split-page-container';
import { StackScreenProps } from '@react-navigation/stack';
import { TabCardApplicationActionSheet } from '@/components/payments/tab-card-application-action-sheet';
import { TabCardDetails } from '@/components/payments/types';
import { TabCardEnrollmentBannerLink } from '@/components/payments/tab-card-enrollment-banner-link';
import { Typography } from '@usepepper/ui/atoms/typography';
import { TYPOGRAPHY } from '@/styles';
import { UnpaidInvoiceComponent } from '@/components/payments/unpaid-invoice-component';
import { UnpaidInvoiceListItem } from '@/components/payments/unpaid-invoice-list-item';
import { UnpaidInvoiceTotalComponent } from '@/components/payments/unpaid-invoice-total-component';
import {
  UnpaidItemType,
  usePaymentsContext,
} from '@/contexts/payments-context';
import {
  useAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useGenerateStatement } from '@/hooks/payments/use-generate-statement';
import { useGetPaymentHistoryQuery } from '@/hooks/use-get-payment-history-query';
import { useGetPaymentLinks } from '@/hooks/payments/use-get-payment-links';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewFeatureIndicator } from '@/hooks/use-new-feature-indicator';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePayCapability } from '@/hooks/payments/use-pay-capability';
import { useTabCardBanner } from '@/hooks/payments/use-tab-card-banner';
import { useToasts } from '@/contexts/toast-context';
import { WalletActionSheet } from '@/components/payments/wallet-action-sheet';

type Props = StackScreenProps<PaymentsStackParams, PAYMENT_ROUTES.Base>;

/**
 * Desktop payments screen
 *
 * Note: when making changes to this screen ensure that `payments-screen` is
 * also updated or the mobile and web experiences will diverge from one another.
 */
export const SplitPaymentsScreen: React.FC<Props> = ({ route }) => {
  const {
    allUnpaidItems,
    autoPayEnabled,
    autoShowModal,
    enabledPaymentMethods,
    ftniEnabled,
    currencyCode,
    loading: paymentsContextLoading,
    paymentMethods,
    plaidEnabled,
    useV2Invoices,
  } = usePaymentsContext();
  const {
    supplierEmployeeRole,
    selectedRestaurantRelationship: {
      appPaymentsEnabled,
      employeeRole,
      supplierPaymentsStatus,
      showPaymentLinks,
      onDemandPaymentStatementEnabled,
    },
    user,
  } = useAuthenticatedUserContext();
  const { isSmallLayout } = useLayoutContext();
  const { canRecordCashPayment, canRecordCheckPayment, isDistributorUser } =
    usePayCapability({
      enabledPaymentMethods,
      restaurantEmployeeRole: employeeRole,
      showPaymentLinks,
      supplierEmployeeRole,
    });
  // Bank deposits are where a distributor employee banks what they collected
  // in person, so it follows the same flags as recording cash and checks.
  const showBankDeposits =
    isDistributorUser && (canRecordCashPayment || canRecordCheckPayment);
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();
  const [splitContainerStyles] = useSplitContainerStyles();
  const isOfflineModeActive = useOfflineModeActive();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();

  const rawTabCardConfig = useDynamicConfigValue('tab_card_config');
  const tabCardDetails: TabCardDetails | null =
    rawTabCardConfig?.enabled &&
    rawTabCardConfig?.sign_up_url &&
    rawTabCardConfig?.cashback_percentage !== null &&
    rawTabCardConfig?.cashback_percentage !== undefined
      ? {
          cashbackPercentage: rawTabCardConfig.cashback_percentage,
          distributorName: partnerSupplier.fullName,
          ...(rawTabCardConfig?.image_url && {
            imageUrl: rawTabCardConfig.image_url,
          }),
          signUpUrl: rawTabCardConfig.sign_up_url,
        }
      : null;

  const { dismissBanner: dismissTabBanner, isDismissed: isTabBannerDismissed } =
    useTabCardBanner(restaurantUUID);
  const hasTabPaymentMethod = paymentMethods.some(
    (pm) => pm.type === PaymentMethodType.TAB,
  );
  const showTabBanner =
    Boolean(tabCardDetails) &&
    !isTabBannerDismissed &&
    !hasTabPaymentMethod &&
    Boolean(employeeRole || supplierEmployeeRole);
  const showWalletTabBanner =
    Boolean(tabCardDetails) &&
    !hasTabPaymentMethod &&
    Boolean(employeeRole || supplierEmployeeRole);

  const [selectedPaymentUUID, setSelectedPaymentUUID] = useState<string | null>(
    null,
  );
  const [selectedPaymentLink, setSelectedPaymentLink] =
    useState<PaymentLink | null>(null);
  const [selectedUnpaidItem, setSelectedUnpaidItem] =
    useState<UnpaidItemType | null>(null);
  const [selectedTab, setSelectedTab] = useState(
    appPaymentsEnabled ? PaymentTabRoutes.Unpaid : PaymentTabRoutes.History,
  );
  const [showAutoPayModal, setShowAutoPayModal] = useState(false);
  const [showTabCardActionSheet, setShowTabCardActionSheet] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showMoreActionsSheet, setShowMoreActionsSheet] = useState(false);

  const {
    fetchingMore,
    fetchMore,
    hasResponse,
    loading,
    paymentBatches,
    refresh,
    refreshing,
  } = useGetPaymentHistoryQuery();

  const isSupplierEmployee = Boolean(supplierEmployeeRole);

  const {
    loading: paymentLinksLoading,
    paymentLinks,
    refresh: refreshPaymentLinks,
    refreshing: refreshingPaymentLinks,
  } = useGetPaymentLinks(
    restaurantUUID,
    supplierUUID,
    !isSupplierEmployee || !showPaymentLinks,
  );

  const [generateStatement] = useGenerateStatement();

  useEffect(() => {
    if (selectedTab === PaymentTabRoutes.History) {
      setSelectedPaymentUUID(
        paymentBatches.length && !loading ? paymentBatches[0].uuid : null,
      );
    }
  }, [loading, paymentBatches, selectedTab]);

  useEffect(() => {
    if (selectedTab === PaymentTabRoutes.PaymentLinks) {
      setSelectedPaymentLink(
        paymentLinks.length && !paymentLinksLoading ? paymentLinks[0] : null,
      );
    }
  }, [loading, paymentLinks, paymentLinksLoading, selectedTab]);

  useEffect(() => {
    if (autoShowModal) {
      setSelectedTab(PaymentTabRoutes.Unpaid);
    }
  }, [autoShowModal]);

  useEffect(() => {
    if (route.params?.unpaidInvoice) {
      setSelectedTab(PaymentTabRoutes.Unpaid);
      setSelectedUnpaidItem(route.params.unpaidInvoice);
    }
  }, [route.params?.unpaidInvoice]);

  // Disable history tab if distributor uses FTNI for payments
  const showHistoryTab = !ftniEnabled;

  const unpaidInvoiceComponent = (
    <CreditRequestInvoiceStoreProvider>
      <UnpaidInvoiceComponent
        contentSpacing={Spacing.SPACE_LARGE}
        onPaymentSuccessAutoPayEnrollment={() => setShowAutoPayModal(true)}
        onReviewBannerPress={() => setShowTabCardActionSheet(true)}
        renderListItem={({ isChecked, onCheck, unpaidItem }) => (
          <UnpaidInvoiceListItem
            bottomLeftTypography="paragraph-sm"
            bottomRightTypography="paragraph-sm"
            containerStyle={styles.listItemContainer}
            currencyCode={currencyCode}
            isChecked={isChecked}
            isSelected={unpaidItem.uuid === selectedUnpaidItem?.uuid}
            onCheck={onCheck}
            onPress={() => setSelectedUnpaidItem(unpaidItem)}
            showCheckbox={
              !!employeeRole ||
              (!!supplierEmployeeRole && !employeeRole && showPaymentLinks)
            }
            topLeftTypography="paragraph-md-600"
            topRightTypography="paragraph-md"
            unpaidItem={unpaidItem}
          />
        )}
        reviewBanner={({ jumpToTabCardApplication }) =>
          showTabBanner && tabCardDetails ? (
            <TabCardEnrollmentBannerLink
              onDismiss={async () => {
                const dismissCount = await dismissTabBanner();
                Analytics.track(
                  'payment_review.tab_card_banner_dismiss_button.press',
                  { dismissCount },
                );
              }}
              onPress={() => {
                Analytics.track('payment_review.tab_card_banner.press');
                jumpToTabCardApplication();
              }}
              tabCardDetails={tabCardDetails}
            />
          ) : null
        }
        tabCardDetails={tabCardDetails}
        walletFooterBanner={({ jumpToTabCardApplication }) =>
          showWalletTabBanner && tabCardDetails ? (
            <TabCardEnrollmentBannerLink
              onPress={() => {
                Analytics.track('wallet.tab_card_banner.press');
                jumpToTabCardApplication();
              }}
              tabCardDetails={tabCardDetails}
            />
          ) : null
        }
      />
    </CreditRequestInvoiceStoreProvider>
  );

  const hasPayments = hasResponse && paymentBatches.length;

  const splitHistoryComponent =
    loading && !fetchingMore && !hasResponse && !refreshing ? (
      <View style={splitContainerStyles.loadingContainer}>
        <ActivityIndicator />
      </View>
    ) : !hasResponse && isOfflineModeActive ? null : hasPayments ? (
      <PaymentHistoryList
        ListFooterComponent={
          <View style={splitContainerStyles.listFooter}>
            {fetchingMore ? <ActivityIndicator /> : null}
          </View>
        }
        listSeparatorSpacing={Spacing.SPACE_LARGE}
        onEndReached={fetchMore}
        paymentBatches={paymentBatches}
        refreshControl={
          <RefreshControl onRefresh={refresh} refreshing={refreshing} />
        }
        renderListItem={(payment) => (
          <PaymentHistoryListItem
            containerStyle={styles.listItemContainer}
            currencyCode={currencyCode}
            isSelected={payment.uuid === selectedPaymentUUID}
            onPress={() => setSelectedPaymentUUID(payment.uuid)}
            payment={payment}
            topLeftTypography="paragraph-md-600"
            topRightTypography="paragraph-md"
          />
        )}
        selectedPaymentUUID={selectedPaymentUUID}
      />
    ) : (
      <EmptyStateMinimal
        children={
          <Text style={[TYPOGRAPHY.medium, styles.emptyStateText]}>
            {i18n.t('payment_history_empty_message')}
          </Text>
        }
        containerStyle={styles.emptyStateContainer}
        ImageComponent={PaymentHistoryEmptyStateIcon}
        text={i18n.t('payment_history_empty_title')}
        textStyle={TYPOGRAPHY.h2}
      />
    );

  const splitPaymentLinksComponent =
    paymentLinksLoading && !refreshingPaymentLinks ? (
      <View style={splitContainerStyles.loadingContainer}>
        <ActivityIndicator />
      </View>
    ) : (
      <PaymentLinksComponent
        contentSpacing={Spacing.SPACE_LARGE}
        paymentLinks={paymentLinks ?? []}
        refreshControl={
          <RefreshControl
            onRefresh={refreshPaymentLinks}
            refreshing={refreshingPaymentLinks}
          />
        }
        renderListItem={(paymentLink) => (
          <PaymentLinksListItem
            bottomLeftTypography="paragraph-sm"
            bottomRightTypography="paragraph-sm"
            containerStyle={styles.listItemContainer}
            currencyCode={currencyCode}
            isSelected={paymentLink.id === selectedPaymentLink?.id}
            onPress={() => setSelectedPaymentLink(paymentLink)}
            paymentLink={paymentLink}
            topLeftTypography="paragraph-md-600"
            topRightTypography="paragraph-md"
          />
        )}
        selectedPaymentLink={selectedPaymentLink!}
      />
    );

  const selectedUnpaidItemComponent = selectedUnpaidItem ? (
    selectedUnpaidItem?.type === PaymentItemType.Invoice ? (
      useV2Invoices ? (
        <InvoiceDetailComponent
          invoiceUUID={selectedUnpaidItem.uuid}
          navigationHeaderContainerStyle={styles.navigationHeaderContainer}
        />
      ) : (
        <OrderDetailComponent
          invoiceButtonPosition={isSmallLayout ? 'bottom' : 'top'}
          navigationHeaderContainerStyle={styles.navigationHeaderContainer}
          // orderUUID is required for suppliers not on v2 invoices
          orderUUID={selectedUnpaidItem.orderUUID!}
        />
      )
    ) : (
      <CreditMemoDetailComponent creditMemoUUID={selectedUnpaidItem.uuid} />
    )
  ) : (
    <EmptyStateMinimal
      ImageComponent={SelectedInvoiceEmptyStateIcon}
      text={i18n.t('selected_invoice_empty_state.message')}
    />
  );

  const displayWallet =
    supplierPaymentsStatus === PaymentsStatus.Enabled &&
    (appPaymentsEnabled || plaidEnabled) &&
    !ftniEnabled;

  const { showNewIndicator, clearNewIndicator } = useNewFeatureIndicator(
    NewFeature.AUTOPAY,
  );

  const onWalletPress = () => {
    Analytics.track('payments_screen.wallet.press');
    setShowWalletModal(true);
  };

  const onBankDepositsPress = () => {
    Analytics.track('payments_screen.bank_deposits.press');
    setSelectedTab(PaymentTabRoutes.BankDeposits);
  };

  const onViewStatementPress = useCallback(async () => {
    Analytics.track('payments_screen.view_statement.press');
    try {
      const response = await generateStatement({
        variables: {
          employeeUUID: user.uuid,
          restaurantUUID,
          supplierUUID,
        },
      });
      if (response.data?.generatePaymentStatementLink) {
        Linking.openURL(
          response.data.generatePaymentStatementLink.payment_statement_url,
        );
      }
    } catch (e) {
      if (e instanceof ApolloError && getMessageFromGQLError(e)) {
        showErrorToast(getMessageFromGQLError(e));
      } else {
        showErrorToast();
      }
    }
  }, [
    generateStatement,
    restaurantUUID,
    showErrorToast,
    supplierUUID,
    user.uuid,
  ]);

  const onTransactionHistoryPress = () => {
    Analytics.track('payments_screen.transaction_history.press');
    setSelectedTab(PaymentTabRoutes.History);
  };

  const onPaymentLinksPress = () => {
    Analytics.track('payments_screen.payment_links.press');
    setSelectedTab(PaymentTabRoutes.PaymentLinks);
  };

  const unpaidActions = usePaymentsScreenMoreActions({
    onBankDepositsPress: showBankDeposits ? onBankDepositsPress : null,
    onPaymentLinksPress:
      showPaymentLinks && supplierEmployeeRole !== null
        ? onPaymentLinksPress
        : null,
    onTransactionHistoryPress: showHistoryTab
      ? onTransactionHistoryPress
      : null,
    onViewStatementPress: onDemandPaymentStatementEnabled
      ? onViewStatementPress
      : null,
    onWalletPress: displayWallet ? onWalletPress : null,
  });

  const historyOnlyActions = usePaymentsScreenMoreActions({
    onBankDepositsPress: showBankDeposits ? onBankDepositsPress : null,
    onViewStatementPress: onDemandPaymentStatementEnabled
      ? onViewStatementPress
      : null,
    onWalletPress: displayWallet ? onWalletPress : null,
  });

  const moreActions = appPaymentsEnabled ? unpaidActions : historyOnlyActions;

  // Toggle header between balance view (unpaid) and list view (history, payment
  // links)
  const isUnpaidTab = selectedTab === PaymentTabRoutes.Unpaid;

  const actions = useMemo(() => {
    // Hide header actions on history/payment links tabs
    if (!isUnpaidTab && appPaymentsEnabled) {
      return [];
    }

    let headerActions: Action[] = [];

    // Display autopay icon if autopay is enabled and user is a restaurant employee
    if (autoPayEnabled && employeeRole) {
      headerActions.push({
        Icon: showNewIndicator ? NewAutoPayIcon : AutoPayIcon,
        onPress: () => {
          if (showNewIndicator) {
            clearNewIndicator();
          }
          setShowAutoPayModal(true);
        },
        testID: showNewIndicator ? 'new-autopay-icon' : 'autopay-icon',
      });
    }

    if (moreActions.length > 0) {
      headerActions.push({
        component: <PaymentsScreenActions actions={moreActions} />,
      });
      return headerActions;
    }

    return headerActions;
  }, [
    appPaymentsEnabled,
    autoPayEnabled,
    clearNewIndicator,
    employeeRole,
    isUnpaidTab,
    moreActions,
    showNewIndicator,
  ]);

  const navigationHeaderProps: NavigationHeaderProps = isUnpaidTab
    ? {
        contentStyle: {
          height: 'auto',
          marginBottom: 0,
        },
        showShadow: false,
      }
    : {
        ...(appPaymentsEnabled
          ? {
              backButtonLabel: i18n.t('back_to', { name: i18n.t('invoices') }),
              onBackPress: () => setSelectedTab(PaymentTabRoutes.Unpaid),
            }
          : {}),
        showShadow: false,
      };

  const title = isUnpaidTab ? (
    <Typography variant="paragraph-md">{i18n.t('total_balance')}</Typography>
  ) : selectedTab === PaymentTabRoutes.BankDeposits ? (
    i18n.t('bank_deposits.title')
  ) : selectedTab === PaymentTabRoutes.History ? (
    i18n.t('transaction_history')
  ) : (
    i18n.t('payment_links')
  );

  return (
    <>
      <SplitPageContainer
        actions={actions}
        navigationHeaderProps={navigationHeaderProps}
        primaryComponent={
          selectedTab === PaymentTabRoutes.Unpaid ? (
            <>
              {paymentsContextLoading ? null : (
                <View style={styles.unpaidHeader}>
                  <UnpaidInvoiceTotalComponent
                    currencyCode={currencyCode}
                    items={allUnpaidItems}
                  />
                  {showTabBanner && tabCardDetails ? (
                    <TabCardEnrollmentBannerLink
                      onDismiss={async () => {
                        const dismissCount = await dismissTabBanner();
                        Analytics.track(
                          'unpaid_invoices.tab_card_banner_dismiss_button.press',
                          { dismissCount },
                        );
                      }}
                      onPress={() => {
                        Analytics.track(
                          'unpaid_invoices.tab_card_banner.press',
                        );
                        setShowTabCardActionSheet(true);
                      }}
                      tabCardDetails={tabCardDetails}
                    />
                  ) : null}
                </View>
              )}
              <PaymentBannerContainer />
              {unpaidInvoiceComponent}
            </>
          ) : selectedTab === PaymentTabRoutes.BankDeposits ? (
            <BankDepositsContent />
          ) : selectedTab === PaymentTabRoutes.PaymentLinks ? (
            splitPaymentLinksComponent
          ) : (
            splitHistoryComponent
          )
        }
        title={title}
      >
        {isOfflineModeActive ? (
          <OfflineComponent />
        ) : selectedTab === PaymentTabRoutes.Unpaid && allUnpaidItems.length ? (
          selectedUnpaidItemComponent
        ) : selectedTab === PaymentTabRoutes.History && selectedPaymentUUID ? (
          <PaymentDetailComponent paymentBatchUUID={selectedPaymentUUID} />
        ) : selectedTab === PaymentTabRoutes.PaymentLinks &&
          selectedPaymentLink ? (
          <PaymentLinkDetailComponent
            paymentLinkUUID={selectedPaymentLink.id}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        ) : null}
      </SplitPageContainer>
      {showAutoPayModal ? (
        <AutoPayActionSheet
          onClose={() => {
            setShowAutoPayModal(false);
          }}
        />
      ) : null}
      {showWalletModal ? (
        <WalletActionSheet
          footerBanner={({ jumpToTabCardApplication }) =>
            showWalletTabBanner && tabCardDetails ? (
              <TabCardEnrollmentBannerLink
                onPress={() => {
                  Analytics.track('wallet.tab_card_banner.press');
                  jumpToTabCardApplication();
                }}
                tabCardDetails={tabCardDetails}
              />
            ) : null
          }
          onClose={() => {
            setShowWalletModal(false);
          }}
          tabCardDetails={tabCardDetails}
        />
      ) : null}
      {showMoreActionsSheet && moreActions.length > 0 && isSmallLayout ? (
        <BaseActionSheet
          contentStyle={styles.actionsContentStyle}
          isVisible
          onClose={() => setShowMoreActionsSheet(false)}
        >
          <List<PaymentsActionListOption>
            data={moreActions}
            onSelect={async (item) => {
              await item.metadata?.onPress();
              setShowMoreActionsSheet(false);
            }}
            renderLeftComponent={(item) => {
              const { Icon, iconProps } = item.metadata!;
              return <Icon height={24} width={24} {...iconProps} />;
            }}
          />
        </BaseActionSheet>
      ) : null}
      {showTabCardActionSheet && tabCardDetails ? (
        <TabCardApplicationActionSheet
          onClose={() => setShowTabCardActionSheet(false)}
          tabCardDetails={tabCardDetails}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  actionsContentStyle: {
    paddingHorizontal: 0,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  emptyStateContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  emptyStateText: {
    lineHeight: 24,
    marginTop: theme.sk.spacing.spaceSMedium,
    textAlign: 'center',
  },
  listItemContainer: {
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  navigationHeaderContainer: {
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    // The animated navigation header on native automatically includes padding
    // so only set it for web
    paddingTop: Platform.OS === 'web' ? theme.sk.spacing.spaceLarge : 0,
  },
  unpaidHeader: {
    gap: theme.sk.spacing.spaceMedium,
    marginBottom: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
}));
